import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Outlet,
  useNavigate,
  useOutlet,
  useOutletContext,
  useParams,
} from 'react-router';
import { ScrollableContainer } from 'components/common/ScrollableContainer';
import { Button } from 'components/common/Button';
import s from './Project.module.scss';
import { useGetProjectsQuery } from 'store/features/project/project-api';
import { selectChatByProjectId } from 'store/features/chat/selectors';
import { transformDate } from 'lib/utils/common';
import { Loader } from 'components/common/Loader/Loader';
import { useQueryError } from 'lib/hooks/useQueryError';
import {
  useMoveChatMutation,
  useRenameChatMutation,
} from 'store/features/chat/chat-api';
import { useAppSelector } from 'store/hooks';
import { Modal } from 'components/common/Modal/Modal';
import { useTranslation } from 'react-i18next';
import { ModalNavigationContext } from 'components/pages/ConsultantPage/ConsultantPage';
import { ChatSummary } from 'types/chat';
import { ReactComponent as MenuIcon } from 'images/menu.svg';
import { useToggle } from 'lib/hooks/useToggle';
import { PortalComponent } from 'components/common/PortalComponent';
import {
  ContextMenu,
  ContextMenuOption,
} from 'components/common/ContextMenu/ContextMenu';
import { ReactComponent as Remove } from 'images/remove.svg';
import { useOutsideClick } from 'lib/hooks/useOutsideClick';

const icons = {
  delete: <Remove className={s['context-menu__item__icon']} />,
  menu: <MenuIcon />,
};

type ChatItemProps = {
  id: ChatSummary['id'];
  name: ChatSummary['name'];
  date: ChatSummary['date'];
  onClick: () => void;
  onDeleteChat: (chatId: string) => void;
};

const MENU_HEIGHT = 180;
const _MENU_WIDTH = 200;
const VIEWPORT_PADDING = 8;

const ChatItem: FC<ChatItemProps> = ({
  id,
  name,
  date,
  onClick,
  onDeleteChat,
}) => {
  const { t } = useTranslation();

  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const { date: formattedDate } = transformDate(date);

  const [isOpenMenu, setIsOpenMenu] = useToggle(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  const handleOpenMenu = (e: React.MouseEvent) => {
    e.stopPropagation();

    const rect = menuBtnRef.current?.getBoundingClientRect();
    if (!rect) return;

    const hasSpaceBelow =
      window.innerHeight - rect.bottom >= MENU_HEIGHT + VIEWPORT_PADDING;

    setMenuPos({
      top: hasSpaceBelow
        ? rect.bottom
        : Math.max(VIEWPORT_PADDING, rect.top - MENU_HEIGHT + 15),
      left: Math.max(VIEWPORT_PADDING, rect.left),
    });

    setIsOpenMenu(true);
  };

  const optionsMenu = useMemo<ContextMenuOption[]>(
    () => [
      {
        id: 0,
        children: (
          <div className={s['context-menu__item']}>
            {icons.delete}
            <span className={s['context-menu__item__name']}>
              {t('contextMenu.remove')}
            </span>
          </div>
        ),
        onClick: () => {
          onDeleteChat(id);
          setIsOpenMenu(false);
        },
      },
    ],
    [id, onDeleteChat, setIsOpenMenu, t],
  );

  useOutsideClick({
    ref: menuRef,
    handlerClose: () => setIsOpenMenu(false),
    attached: isOpenMenu,
  });

  return (
    <li className={s.item}>
      <div className={s['item__info']} onClick={onClick}>
        <span className={s['item__name']}>{name}</span>
        <span className={s['item__date']}>{formattedDate}</span>
      </div>

      <Button
        ref={menuBtnRef}
        className={s['item__menu-btn']}
        onClick={handleOpenMenu}
      >
        {icons.menu}
      </Button>

      {isOpenMenu && (
        <PortalComponent>
          <div
            ref={menuRef}
            style={{
              position: 'fixed',
              top: menuPos.top,
              left: menuPos.left,
              zIndex: 3,
            }}
          >
            <ContextMenu className={s['context-menu']} options={optionsMenu} />
          </div>
        </PortalComponent>
      )}
    </li>
  );
};

export const Project: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const hasOutlet = useOutlet();

  const { closeRouteModal } = useOutletContext<ModalNavigationContext>();

  const chats = useAppSelector((state) =>
    selectChatByProjectId(state, projectId),
  );

  const {
    data: projects,
    isLoading,
    isSuccess,
    isError,
  } = useGetProjectsQuery();

  const [
    removeChat,
    {
      isLoading: isRemoveLoading,
      isError: isRemoveError,
      error: removeError,
      reset: resetRemove,
    },
  ] = useMoveChatMutation();

  const [
    renameChat,
    {
      isLoading: isRenameLoading,
      isError: isRenameError,
      error: renameError,
      reset: resetRename,
    },
  ] = useRenameChatMutation();

  const { errorMessage } = useQueryError(
    isRemoveError || isRenameError,
    removeError || renameError,
  );

  const _handleRenameChat = useCallback(
    (chatId: string, newName: string) => {
      try {
        const name = newName.trim();
        if (isRenameLoading || !name) return;

        renameChat({ chatId, name });
      } catch {}
    },
    [isRenameLoading, renameChat],
  );

  const handleRemoveChat = useCallback(
    (chatId: string) => {
      if (isRemoveLoading) return;

      removeChat({ chatId, projectId: null });
    },
    [isRemoveLoading, removeChat],
  );

  const handleOpenChat = useCallback(
    (id: string) => {
      navigate(`/chat/${id}`);
    },
    [navigate],
  );

  useEffect(() => {
    if (isRemoveError) {
      const timeout = setTimeout(resetRemove, 2000);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [isRemoveError, resetRemove]);

  useEffect(() => {
    if (isRenameError) {
      const timeout = setTimeout(resetRename, 2000);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [isRenameError, resetRename]);

  if (isLoading) return <>{t('common.loading')}</>;
  if (!isSuccess || isError) return <>{t('common.error')}</>;

  const project = projects.find((p) => p.id === projectId);
  if (!project) return <>{t('project.errors.noProjectFound')}</>;

  if (hasOutlet) return <Outlet context={project} />;

  let scrollContent: React.ReactNode;
  if (isRemoveError || isRenameError) {
    scrollContent = <span className={s.error}> {errorMessage}</span>;
  } else if (chats.length) {
    scrollContent = (
      <ul className={s['chat-list']}>
        {chats.map((chat) => (
          <ChatItem
            key={chat.id}
            id={chat.id}
            name={chat.name}
            date={chat.date}
            onClick={() => handleOpenChat(chat.id)}
            onDeleteChat={handleRemoveChat}
          />
        ))}
      </ul>
    );
  } else {
    scrollContent = t('project.noChats');
  }

  return (
    <Modal title={t('project.title')} onClose={closeRouteModal}>
      <div className={s.container}>
        <div className={s['project-header']}>
          <div className={s['project-icon']}>{project.name[0]}</div>
          <span className={s['project-name']}>{project.name}</span>
        </div>

        <ScrollableContainer scrollHeight='30rem' className={s.scroll}>
          {scrollContent}
        </ScrollableContainer>

        {/* <Button onClick={handleNewChat} isAccent={true} isRounded={true}>
          {t('project.addChat')}{' '}
        </Button> */}
      </div>

      {(isRemoveLoading || isRenameLoading) && <Loader />}
    </Modal>
  );
};
